// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  CallSplitRounded,
  CloseRounded,
  DeleteOutlineRounded,
  EditRounded,
} from '@/constants/hardfin-standard-icons';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import { ChargeEditForm } from '@/project/components/charge-edit-form';
import {
  ChargeFormDateValue,
  chargeDateKey,
} from '@/project/utilities/charge-date';
import { ChargeFormValue } from '@/project/utilities/generate-charges';
import { formatNumber } from '@/utilities/number';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';
import { memo, useMemo } from 'react';

interface Props {
  charge: ChargeFormValue;
  // Label decorated with "(n/total)" when the charge is part of a split
  // sibling group. Computed upstream where the full charge list is
  // visible; the row itself never adds or strips a decoration.
  displayLabel: string;
  currencyCode: IsoCurrencyCode;
  isEditing: boolean;
  isDisabled: boolean;
  onStartEdit: () => void;
  onStopEdit: () => void;
  onUpdate: (updated: ChargeFormValue) => void;
  onDelete: () => void;
  onSplit: () => void;
}

const STATUS_COLORS: Record<
  string,
  'default' | 'primary' | 'success' | 'warning' | 'error'
> = {
  PLANNED: 'default',
  BILLABLE: 'primary',
  INVOICED: 'success',
  CANCELLED: 'error',
};

function formatServicePeriod(
  start: ChargeFormDateValue,
  end: ChargeFormDateValue
): string {
  const startKey = chargeDateKey(start);
  const endKey = chargeDateKey(end);
  if (!startKey && !endKey) return '';
  if (!startKey || !endKey) {
    const date = startKey ?? endKey;
    return DateTime.fromISO(date!).toFormat('LLL d, yyyy');
  }
  if (startKey === endKey) {
    return DateTime.fromISO(startKey).toFormat('LLL d, yyyy');
  }
  return `${DateTime.fromISO(startKey).toFormat('LLL d, yyyy')} \u2013 ${DateTime.fromISO(endKey).toFormat('LLL d, yyyy')}`;
}

// Charge-row copy is per-charge; the milestone phase editor uses a sibling
// "Phase is invoiced ..." string so each surface speaks to the entity it
// renders. Keep these two strings distinct on purpose.
const INVOICED_LOCKED_TOOLTIP =
  'Charge is invoiced — remove the charge from the invoice to unlock it.';

export const ChargeRow = memo(function ChargeRow(props: Props) {
  const isInvoiced = props.charge.status === 'INVOICED';
  const isImmutable = isInvoiced || props.charge.status === 'CANCELLED';
  const canSplit = props.charge.quantity >= 2 && !isImmutable;

  const servicePeriod = useMemo(
    () =>
      formatServicePeriod(
        props.charge.servicePeriodStart,
        props.charge.servicePeriodEnd
      ),
    [props.charge.servicePeriodStart, props.charge.servicePeriodEnd]
  );

  // PER_UNIT rows show qty × unit_price computed inline so the displayed
  // total always matches the breakdown on the left. The BE enforces
  // unit_price × qty == total_price on save, so the inline value also
  // agrees with the stored total. Milestone / FIXED / total_override rows
  // carry a null unit_price — fall back to the stored total_price there.
  const displayTotal = useMemo(() => {
    if (
      props.charge.pricingMode === 'PER_UNIT' &&
      props.charge.unitPrice !== null
    ) {
      const unitPrice =
        parseCurrencyAmount(props.charge.unitPrice.amount) ??
        new CurrencyDecimal(0);
      return unitPrice.times(props.charge.quantity);
    }

    return (
      parseCurrencyAmount(props.charge.totalPrice.amount) ??
      new CurrencyDecimal(0)
    );
  }, [
    props.charge.pricingMode,
    props.charge.unitPrice,
    props.charge.quantity,
    props.charge.totalPrice,
  ]);

  const handleSave = (updated: ChargeFormValue) => {
    props.onUpdate(updated);
    props.onStopEdit();
  };

  return (
    <Box sx={{ py: 0.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {/* Left: status chip + label + service period */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Chip
            label={
              props.charge.status.charAt(0) +
              props.charge.status.slice(1).toLowerCase()
            }
            color={STATUS_COLORS[props.charge.status] ?? 'default'}
          />
          {props.displayLabel.length > 30 ? (
            <Tooltip title={props.displayLabel} arrow>
              <Typography variant="body2" noWrap sx={{ maxWidth: '30ch' }}>
                {props.displayLabel}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2" noWrap>
              {props.displayLabel}
            </Typography>
          )}
          {servicePeriod && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {servicePeriod}
            </Typography>
          )}
        </Box>

        {/* Right: qty breakdown, amount, actions */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}
        >
          {props.charge.pricingMode === 'PER_UNIT' &&
            props.charge.unitPrice !== null && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {formatNumber(props.charge.quantity)} ×{' '}
                {formatCurrencyValue({
                  amount: new Decimal(props.charge.unitPrice.amount),
                  isoCode: props.charge.unitPrice.isoCode,
                })}
              </Typography>
            )}
          <Typography
            variant="body2"
            noWrap
            sx={{ minWidth: 64, textAlign: 'right' }}
          >
            {formatCurrencyValue({
              amount: displayTotal,
              isoCode: props.charge.totalPrice.isoCode,
            })}
          </Typography>
          <Tooltip
            title={isInvoiced ? INVOICED_LOCKED_TOOLTIP : 'Edit charge'}
            arrow
          >
            <span>
              <IconButton
                size="small"
                aria-label="Edit charge"
                disabled={isImmutable || props.isDisabled}
                onClick={props.isEditing ? props.onStopEdit : props.onStartEdit}
              >
                {props.isEditing ? <CloseRounded /> : <EditRounded />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={isInvoiced ? INVOICED_LOCKED_TOOLTIP : 'Split charge'}
            arrow
          >
            <span>
              <IconButton
                size="small"
                aria-label="Split charge"
                disabled={!canSplit || props.isDisabled}
                onClick={props.onSplit}
              >
                <CallSplitRounded />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={isInvoiced ? INVOICED_LOCKED_TOOLTIP : 'Delete charge'}
            arrow
          >
            <span>
              <IconButton
                size="small"
                aria-label="Delete charge"
                disabled={isImmutable || props.isDisabled}
                onClick={props.onDelete}
              >
                <DeleteOutlineRounded />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {props.isEditing && (
        <ChargeEditForm
          charge={props.charge}
          currencyCode={props.currencyCode}
          onSave={handleSave}
          onCancel={props.onStopEdit}
        />
      )}
    </Box>
  );
});
